import { useMemo } from 'react';
import {
  useGetLogisticOperatorsQuery,
  useGetUnscopedLogisticOperatorsQuery,
} from '../../LogisticOperator/services/logistic-operators.service';
import { useHeadquartersOptions } from '../../LogisticOperator/hooks';
import {
  useGetCommercesQuery,
  useGetLogisticOperatorAssociationsQuery,
} from '../../Commerce/services';
import { useGetAllBranchesQuery } from '../../Commerce/services/branches.service';
import { useGetRolesQuery } from '../services/roles.service';
import { SelectInputOption } from '@/components/inputs/SelectInput';
import {
  EXCLUDED_EXTERNAL_OPERATOR_ID,
  ROLES,
  userHasPermission,
} from '@/components/security/AppRoles';
import { useUserInfo } from '@/hooks';
import { RoleRestriction } from '../enums';
import type { InviteUserType } from '../models';

export type ScopeLogisticOperatorRow = {
  id: string;
  fullName: string;
  parentId?: string | null;
};

export type ScopeCommerceRow = { id: string; name: string };

export type ScopeAssociationRow = {
  logisticOperatorId: string;
  commerceId: string;
};

/** Opciones de suboperadores filtradas por operadores padre seleccionados (catálogo ya cargado). */
export function buildSubOperatorSelectOptions(
  operators: ScopeLogisticOperatorRow[] | undefined,
  selectedParentOperatorIds: string[]
): SelectInputOption[] {
  if (!operators?.length || selectedParentOperatorIds.length === 0) {
    return [];
  }
  return operators
    .filter(
      (operator) =>
        operator.parentId &&
        selectedParentOperatorIds.includes(operator.parentId)
    )
    .map((operator) => ({
      label: operator.fullName,
      value: operator.id,
    }));
}

/** Opciones de comercios filtradas por operadores seleccionados (vía asociaciones). */
export function buildCommerceSelectOptions(
  commerces: ScopeCommerceRow[] | undefined,
  associations: ScopeAssociationRow[] | undefined,
  selectedOperatorIds: string[]
): SelectInputOption[] {
  if (!commerces?.length) {
    return [];
  }
  let filteredCommerces = commerces;
  if (selectedOperatorIds.length > 0 && associations?.length) {
    const associatedCommerceIds = new Set(
      associations
        .filter((assoc) =>
          selectedOperatorIds.includes(assoc.logisticOperatorId)
        )
        .map((assoc) => assoc.commerceId)
    );
    filteredCommerces = commerces.filter((commerce) =>
      associatedCommerceIds.has(commerce.id)
    );
  }
  return filteredCommerces.map((commerce) => ({
    label: commerce.name,
    value: commerce.id,
  }));
}

interface UseInviteUserOptionsProps {
  selectedCommerceIds?: string[];
  selectedOperatorIds?: string[];
  userType?: InviteUserType;
}

const useInviteUserOptions = ({
  selectedCommerceIds = [],
  selectedOperatorIds = [],
  userType,
}: UseInviteUserOptionsProps = {}) => {
  const { data: rolesData, isLoading: isLoadingRoles } = useGetRolesQuery(
    userType ? { restrictions: userType } : { restrictions: undefined },
    { skip: !userType }
  );

  const { options: headquarterOptions, isLoading: isLoadingHeadquarterOperators } =
    useHeadquartersOptions();

  const isCommerceType = userType === RoleRestriction.COMMERCE;

  const { data: operatorsData, isLoading: isLoadingOperators } =
    useGetLogisticOperatorsQuery(
      { paginated: { page: 1, pageSize: 1000 } },
      { skip: isCommerceType }
    );

  const isExternalOperatorType = userType === RoleRestriction.EXTERNAL_OPERATOR;
  const isSoydeliveryOperatorType = userType === RoleRestriction.OPERATOR;

  const { permissions } = useUserInfo();
  const canPickAnyOperator =
    isSoydeliveryOperatorType &&
    userHasPermission(permissions, ROLES.BUSINESS.admin);

  const {
    data: unscopedOperatorsData,
    isLoading: isLoadingUnscopedOperators,
  } = useGetUnscopedLogisticOperatorsQuery(
    { paginated: { page: 1, pageSize: 1000 } },
    { skip: !isExternalOperatorType && !canPickAnyOperator }
  );

  const { data: associationsData, isLoading: isLoadingAssociations } =
    useGetLogisticOperatorAssociationsQuery(
      { paginated: { page: 1, pageSize: 10000 }, filters: {} },
      { skip: isCommerceType }
    );

  const { data: commercesData, isLoading: isLoadingCommerces } =
    useGetCommercesQuery(
      { paginated: { page: 1, pageSize: 1000 } },
      { skip: isCommerceType }
    );

  const { data: branchesData, isLoading: isLoadingBranches } =
    useGetAllBranchesQuery(
      { paginated: { page: 1, pageSize: 1000 } },
      { skip: isCommerceType }
    );

  const mainOperatorsOptions: SelectInputOption[] = useMemo(() => {
    if (canPickAnyOperator) {
      return (unscopedOperatorsData?.items ?? [])
        .filter((operator) => operator.headquarter)
        .map((operator) => ({ label: operator.fullName, value: operator.id }));
    }
    if (isSoydeliveryOperatorType) {
      return (headquarterOptions as SelectInputOption[]).filter(
        (operator) => operator.value === EXCLUDED_EXTERNAL_OPERATOR_ID
      );
    }
    return headquarterOptions as SelectInputOption[];
  }, [
    canPickAnyOperator,
    unscopedOperatorsData?.items,
    headquarterOptions,
    isSoydeliveryOperatorType,
  ]);

  const operatorsCatalogItems = canPickAnyOperator
    ? unscopedOperatorsData?.items
    : operatorsData?.items;

  const subOperatorsOptions: SelectInputOption[] = useMemo(() => {
    if (!operatorsCatalogItems) return [];

    if (selectedOperatorIds.length > 0) {
      return operatorsCatalogItems
        .filter(
          (operator) =>
            operator.parentId && selectedOperatorIds.includes(operator.parentId)
        )
        .map((operator) => ({
          label: operator.fullName,
          value: operator.id,
        }));
    }

    return [];
  }, [operatorsCatalogItems, selectedOperatorIds]);

  const externalOperatorsCatalog: ScopeLogisticOperatorRow[] = useMemo(() => {
    if (!unscopedOperatorsData?.items) return [];

    return unscopedOperatorsData.items
      .filter(
        (operator) =>
          operator.id !== EXCLUDED_EXTERNAL_OPERATOR_ID &&
          operator.parentId !== EXCLUDED_EXTERNAL_OPERATOR_ID
      )
      .map((operator) => ({
        id: operator.id,
        fullName: operator.fullName,
        parentId: operator.parentId ?? null,
      }));
  }, [unscopedOperatorsData?.items]);

  const externalOperatorsOptions: SelectInputOption[] = useMemo(
    () =>
      externalOperatorsCatalog.map((operator) => ({
        label: `${operator.fullName} · ${
          operator.parentId ? 'Sub-operador' : 'Casa central'
        }`,
        value: operator.id,
      })),
    [externalOperatorsCatalog]
  );

  const commercesOptions: SelectInputOption[] = useMemo(() => {
    if (!commercesData?.items) return [];

    let filteredCommerces = commercesData.items;

    if (selectedOperatorIds.length > 0 && associationsData?.items) {
      const associatedCommerceIds = new Set(
        associationsData.items
          .filter((assoc) => selectedOperatorIds.includes(assoc.logisticOperatorId))
          .map((assoc) => assoc.commerceId)
      );

      filteredCommerces = commercesData.items.filter((commerce) =>
        associatedCommerceIds.has(commerce.id)
      );
    }

    return filteredCommerces.map((commerce) => ({
      label: commerce.name,
      value: commerce.id,
    }));
  }, [
    commercesData?.items,
    selectedOperatorIds,
    associationsData?.items,
  ]);

  const branchesOptions: SelectInputOption[] = useMemo(() => {
    if (!branchesData?.items) return [];

    let filteredBranches = branchesData.items;

    if (selectedCommerceIds.length > 0) {
      filteredBranches = branchesData.items.filter((branch) =>
        selectedCommerceIds.includes(branch.commerceId)
      );
    }

    return filteredBranches.map((branch) => ({
      label: branch.name,
      value: branch.id,
    }));
  }, [branchesData?.items, selectedCommerceIds]);

  const branchesDataForFiltering = branchesData?.items || [];

  const rolesOptions: SelectInputOption[] = useMemo(() => {
    if (!rolesData || !Array.isArray(rolesData)) return [];
    return rolesData.map((role) => ({
      label: role.description ?? role.name,
      value: role.id,
    }));
  }, [rolesData, userType]);

  return {
    mainOperatorsOptions,
    subOperatorsOptions,
    externalOperatorsOptions,
    externalOperatorsCatalog,
    commercesOptions,
    branchesOptions,
    branchesData: branchesDataForFiltering,
    operatorsCatalog: (operatorsCatalogItems ?? []) as ScopeLogisticOperatorRow[],
    associationsCatalog: (associationsData?.items ??
      []) as ScopeAssociationRow[],
    commercesCatalog: (commercesData?.items ?? []) as ScopeCommerceRow[],
    rolesOptions,
    isLoading:
      isLoadingHeadquarterOperators ||
      isLoadingOperators ||
      isLoadingUnscopedOperators ||
      isLoadingAssociations ||
      isLoadingCommerces ||
      isLoadingBranches ||
      isLoadingRoles,
  };
};

export default useInviteUserOptions;
